import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  CloseOutlined,
  ExportOutlined,
  FileTextOutlined,
  FolderOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PauseOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  SisternodeOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import IconRound from 'assets/logos/icon-round.svg?react';
import BackButton from 'components/buttons/BackButton';
import PatientAccessTierTag from 'components/chats/PatientAccessTierTag';
import CaregiverProfileModal from 'components/modals/CaregiverProfileModal';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import { Separator } from 'components/ui/separator';
import { setConversationStatusValue } from 'hooks/conversationListMutators';
import { useUpdateConversations } from 'hooks/useConversations';
import { patientHistoryQueryOptions, usePatientHistoryCount } from 'hooks/useLoadPatientHistory';
import useSelectConversations from 'hooks/useSelectConversations';
import { cn } from 'lib/utils';
import { SearchIcon } from 'lucide-react';
import posthog from 'posthog-js';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { routes } from 'services/RouteService';
import ApiCaregiversManager from 'services/api/ApiCaregiversManager';
import ApiDoctorConversationsManager, { StatusChangeRequestType } from 'services/api/ApiDoctorConversationsManager';
import { toast } from 'sonner';
import { selectCurrentUser } from 'store/users/selectors';

/** In-conversation message search UI (passed from the page that owns list state). */
export type ConversationTopBarMessageSearch = {
  open: boolean;
  query: string;
  matchIndex: number;
  matchCount: number;
  showPatientTrigger: boolean;
  showInCaregiverMenu: boolean;
  onOpen: () => void;
  onClose: () => void;
  onQueryChange: (value: string) => void;
  onPrevMatch: () => void;
  onNextMatch: () => void;
};

const defaultMessageSearch: ConversationTopBarMessageSearch = {
  open: false,
  query: '',
  matchIndex: 0,
  matchCount: 0,
  showPatientTrigger: false,
  showInCaregiverMenu: false,
  onOpen: () => {},
  onClose: () => {},
  onQueryChange: () => {},
  onPrevMatch: () => {},
  onNextMatch: () => {},
};

export type RecipientHeaderProps = {
  name?: string;
  avatar?: string | React.ReactNode;
  caregiverId?: string;
  patientId?: string;
  // add some props to control what to display
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  showSummaryButton?: boolean;
  showHistoryButton?: boolean;
  showActionsButton?: boolean;
  messageSearch?: ConversationTopBarMessageSearch;
};

/** The buttons of the actions row sit on the brand (inverse) background. */
const actionButtonClassName =
  'text-inverse-foreground shadow-none hover:bg-inverse-foreground/10 hover:text-inverse-foreground';
const actionDetailsClassName = 'mt-1 max-w-[60vw] text-xs leading-tight whitespace-normal text-subtle-foreground';
const menuIndentedItemClassName = 'pl-6';
const menuTopAlignedIconItemClassName = 'items-start [&>.anticon]:mt-1';

const ConversationTopBar: React.FC<RecipientHeaderProps> = ({
  name = undefined,
  avatar = undefined,
  caregiverId = undefined,
  patientId = undefined,
  showBackButton = false,
  onBackButtonClick = undefined,
  showSummaryButton = false,
  showHistoryButton = false,
  showActionsButton = false,
  messageSearch: messageSearchProp,
}) => {
  const messageSearch = messageSearchProp ?? defaultMessageSearch;
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateConversations = useUpdateConversations();

  // Selectors
  const currentUser = useSelector(selectCurrentUser);
  const { conversationId, conversationMetadata } = useSelectConversations();

  const urlParams = useParams();
  // TODO this is likely bad coupling between the router and this component's logic
  const viewedConversationId = urlParams.historicConversation || conversationId;
  const { pathname } = useLocation();
  const isOnDossier = pathname.endsWith('/dossier');

  // State
  const [openProfileModal, setOpenProfileModal] = useState(false);

  function onBackButtonClicked() {
    if (onBackButtonClick) {
      onBackButtonClick();
    }
  }

  const {
    loading: loadingGetCaregiverGroupsList,
    run: getCaregiverGroupsList,
    data,
  } = useRequest(
    () => {
      const patientId = conversationMetadata?.conversation.patient.id;
      if (patientId) {
        return ApiCaregiversManager.getAvailableCaregiverGroupsForPatient({ patientId });
      }
      return ApiCaregiversManager.getAvailableCaregiverGroupsList();
    },
    {
      manual: true,
      onError: () => {
        toast.error(t('caregivers.list.errors.fetch'));
      },
    },
  );

  const { loading: loadingRequestOwnerChange } = useRequest(
    caregiverGroupId => ApiDoctorConversationsManager.requestOwnerChange({ caregiverGroupId, conversationId }),
    {
      manual: true,
      onSuccess: () => {
        toast.success(t('caregivers.actions.requestOwnerChange.success'));
        onBackButtonClicked();
      },
      onError: () => {
        toast.error(t('caregivers.actions.requestOwnerChange.error'));
      },
    },
  );

  const { loading: loadingCreateNewConversationWithGroup, run: createNewConversationWithGroup } = useRequest(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (caregiverGroupId: string, _patientId: string) =>
      ApiDoctorConversationsManager.createNewConversationWithGroup({ caregiverGroupId, conversationId }),
    {
      manual: true,
      onSuccess: (_data, [, patientId]) => {
        toast.success(t('caregivers.actions.createNewConversationWithGroup.success'));
        queryClient.invalidateQueries({ queryKey: patientHistoryQueryOptions(patientId).queryKey });
      },
      onError: () => {
        toast.error(t('caregivers.actions.createNewConversationWithGroup.error'));
      },
    },
  );

  const { loading: loadingRequestCaregiverAssignment, run: requestCaregiverAssignment } = useRequest(
    registeredCaregiverId =>
      ApiDoctorConversationsManager.requestCaregiverAssignment({ registeredCaregiverId, conversationId }),
    {
      manual: true,
      onSuccess: () => {
        toast.success(t('caregivers.actions.requestCaregiverAssignment.success'));
        onBackButtonClicked();
      },
      onError: () => {
        toast.error(t('caregivers.actions.requestCaregiverAssignment.error'));
      },
    },
  );

  const { loading: loadingStatusChange, run: requestStatusChange } = useRequest(
    (newStatus: StatusChangeRequestType) =>
      ApiDoctorConversationsManager.requestConversationStatusChange(conversationId, newStatus),
    {
      manual: true,
      onSuccess: (_data, params) => {
        toast.success(t('caregivers.actions.statusChange.success'));
        // Optimistically update the conversation row's status; the segmented bucket
        // (closed / onHold) is then derived from `status.value` by getConversationType,
        // and the websocket `conversation.status.updated` event will confirm it later.
        updateConversations(setConversationStatusValue(conversationId, params[0]));
        onBackButtonClicked();
      },
      onError: () => {
        toast.error(t('caregivers.actions.statusChange.error'));
      },
    },
  );

  function transfer() {
    getCaregiverGroupsList();
    getCaregiversFromHistory();
  }

  function selectCaregiverGroup(caregiverGroupId: string) {
    createNewConversationWithGroup(caregiverGroupId, conversationMetadata?.conversation.patient.id ?? '');
  }

  function selectCaregiver(caregiverId: string) {
    requestCaregiverAssignment(caregiverId);
  }

  const { run: getCaregiversFromHistory, data: caregiversFromHistory } = useRequest(
    () => ApiDoctorConversationsManager.getInvolvedRegisteredCaregivers(conversationId),
    {
      manual: true,
      onError: () => {
        toast.error(t('caregivers.list.errors.fetch'));
      },
    },
  );

  const normalizedCaregiversFromHistory = React.useMemo(() => {
    const list = caregiversFromHistory || [];
    return list
      .map((rc: any) => ({
        id: rc?.user?.id ?? rc?.id,
        name: rc?.caregiver?.display_name ?? rc?.display_name,
        photo_url: rc?.user?.photo_url ?? rc?.photo_url,
        groups: rc?.caregiver?.caregiver_groups ?? rc?.caregiver_groups ?? [],
      }))
      .filter(c => c.id && c.id !== currentUser?.id);
  }, [caregiversFromHistory, currentUser?.id]);

  function shareConversationToTeam() {
    const apiHost = import.meta.env.VITE_API_SERVER_URL;
    const dashboardUrl = `${apiHost}/dashboard/conversations/${viewedConversationId}/`;

    navigator.clipboard
      .writeText(dashboardUrl)
      .then(() => {
        toast.success(t('chats.actions.shareToTeamSuccess'));
      })
      .catch(() => {
        toast.error(t('global.errors.unknown'));
      });
  }

  const isHeaderClickable = Boolean((patientId && !isOnDossier) || caregiverId);

  function onHeaderClicked() {
    if (patientId && !isOnDossier) {
      navigate(routes.caregiverDossier.route.replace(':conversationId', viewedConversationId));
    } else if (caregiverId) {
      setOpenProfileModal(true);
    }
  }

  const { count: historyCount, loading: historyCountLoading } = usePatientHistoryCount(
    conversationMetadata?.conversation.patient.id ?? '',
  );

  const isActionsMenuBusy =
    loadingGetCaregiverGroupsList ||
    loadingRequestOwnerChange ||
    loadingStatusChange ||
    loadingRequestCaregiverAssignment ||
    loadingCreateNewConversationWithGroup;

  const actionsMenu = (
    <DropdownMenu
      onOpenChange={open => {
        if (open) {
          transfer();
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={actionButtonClassName}
          disabled={isActionsMenuBusy}
          loading={isActionsMenuBusy}
        >
          <PlusCircleOutlined />
          {t('chats.actions.button')}
        </Button>
      </DropdownMenuTrigger>
      {/*
       * Bounded: unbounded, this can grow past the viewport on a conversation
       * with a long caregiver-transfer history — the portal then pushes past
       * the bottom of the screen and the whole document scrolls to reach it,
       * instead of just the menu itself.
       */}
      {/* long caregiver menus scroll instead of overflowing the viewport */}
      <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto [&_.anticon]:text-muted-foreground">
        {messageSearch.showInCaregiverMenu && (
          <>
            <DropdownMenuItem onSelect={() => messageSearch.onOpen()}>
              <SearchOutlined />
              <span>{t('chats.actions.searchMessages')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onSelect={() => shareConversationToTeam()}>
          <ExportOutlined />
          <span>{t('chats.actions.shareToTeam')}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className={menuTopAlignedIconItemClassName} onSelect={() => requestStatusChange('ON_HOLD')}>
          <PauseOutlined />
          <div>
            <span>{t('chats.actions.putOnHold')}</span>
            <div className={actionDetailsClassName}>{t('chats.actions.putOnHoldDetails')}</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className={menuTopAlignedIconItemClassName} onSelect={() => requestStatusChange('CLOSED')}>
          <CheckOutlined />
          <div>
            <span>{t('chats.actions.close')}</span>
            <div className={actionDetailsClassName}>{t('chats.actions.putOnHoldDetails')}</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {normalizedCaregiversFromHistory.length > 0 && (
          <>
            <DropdownMenuItem>
              <SwapOutlined />
              <span className="text-muted-foreground">{t('chats.actions.reassignToCaregiver')}</span>
            </DropdownMenuItem>
            {normalizedCaregiversFromHistory.map(caregiver => (
              <DropdownMenuItem
                key={`assign_caregiver.${caregiver.id}`}
                className={menuIndentedItemClassName}
                onSelect={() => selectCaregiver(caregiver.id)}
              >
                <span className="inline-flex items-center gap-2">
                  <Avatar size="sm">
                    <AvatarImage src={caregiver.photo_url} />
                    <AvatarFallback />
                  </Avatar>
                  <span>
                    {caregiver.name}
                    {Array.isArray(caregiver.groups) && caregiver.groups.length > 0 && (
                      <div className={actionDetailsClassName}>
                        {caregiver.groups.map((g: any) => g.display_name || g.name).join(', ')}
                      </div>
                    )}
                  </span>
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuItem>
          <SisternodeOutlined />
          <span className="text-muted-foreground">{t('chats.actions.transferTo')}</span>
        </DropdownMenuItem>
        {(data || []).map(d => {
          // CaregiverGroupDTO typing doesn't include this field, but the API may return it.
          // Keep runtime behavior while staying type-safe.
          const hasOngoingConversation = Boolean((d as any).has_ongoing_conversation);
          return (
            <DropdownMenuItem
              key={`assign.${d.id}`}
              className={menuIndentedItemClassName}
              disabled={hasOngoingConversation}
              onSelect={() => selectCaregiverGroup(d.id)}
            >
              <span className={hasOngoingConversation ? 'text-muted-foreground opacity-60' : undefined}>
                {d.display_name}
                {hasOngoingConversation && ` - ${t('chats.actions.ongoing', 'En cours')}`}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  function buildPrimaryActionsBar() {
    const components = [];
    if (!currentUser?.is_caregiver && conversationId && conversationMetadata?.conversation.current_owner) {
      // Patients: "Mes documents" button (only show if there's a caregiver assigned)
      components.push(
        <Button
          variant="ghost"
          key="my-documents-button"
          className={actionButtonClassName}
          onClick={() => {
            navigate(routes.patientDocuments.route.replace(':conversationId', conversationId));
            posthog.capture('patient_documents.page_viewed');
          }}
        >
          <FileTextOutlined />
          {t('chats.actions.myDocuments')}
        </Button>,
      );
      if (messageSearch.showPatientTrigger && !messageSearch.open) {
        components.push(
          <Button
            variant="ghost"
            key="search-messages-button"
            aria-label={t('chats.actions.searchMessages')}
            onClick={() => messageSearch.onOpen()}
            className={cn(actionButtonClassName, 'inline-flex items-center gap-1 whitespace-nowrap')}
          >
            <SearchOutlined />
            {t('chats.actions.searchShortLabel')}
          </Button>,
        );
      }
    }
    if (currentUser?.is_caregiver && showSummaryButton) {
      components.push(
        <Button
          variant="ghost"
          key="dossier-button"
          className={actionButtonClassName}
          onClick={() => {
            navigate(routes.caregiverDossier.route.replace(':conversationId', viewedConversationId));
            posthog.capture('patient_file.page_viewed');
          }}
        >
          <FolderOutlined />
          {t('chats.actions.dossier')}
        </Button>,
      );
    }
    if (currentUser?.is_caregiver) {
      if (showHistoryButton) {
        components.push(
          <Button
            variant="ghost"
            key="show-history-button"
            className={actionButtonClassName}
            onClick={() =>
              navigate(routes.patientConversationsHistory.route.replace(':conversationId', conversationId))
            }
          >
            <HistoryOutlined />
            {t('chats.actions.history')}
            {historyCountLoading ? (
              <LoadingOutlined className="ml-1" />
            ) : (
              historyCount > 1 && <Badge variant="secondary">{historyCount}</Badge>
            )}
          </Button>,
        );
      }
      if (showActionsButton) {
        components.push(<React.Fragment key="actions-dropdown">{actionsMenu}</React.Fragment>);
      }
    }
    return components;
  }

  const primaryActionsBar = buildPrimaryActionsBar()
    .flatMap((button, index) => [
      button,
      <Separator
        key={`divider-${index}`}
        orientation="vertical"
        className="self-stretch data-[orientation=vertical]:h-auto"
      />,
    ])
    .slice(0, -1);

  const showActionsRow = messageSearch.open || primaryActionsBar.length > 0;

  return (
    <>
      <div className="z-[100] flex w-full items-center gap-2.5 bg-card px-3 py-3 shadow-sm">
        {openProfileModal && caregiverId && (
          <CaregiverProfileModal caregiverId={caregiverId} open onClose={() => setOpenProfileModal(false)} />
        )}
        {showBackButton && (
          <div className="shrink-0">
            <BackButton onClick={() => onBackButtonClicked()} />
          </div>
        )}
        {avatar && (
          <div className={cn('shrink-0', isHeaderClickable && 'cursor-pointer')} onClick={() => onHeaderClicked()}>
            {typeof avatar === 'string' && avatar.startsWith('http') ? (
              <Avatar>
                <AvatarImage src={avatar} />
                <AvatarFallback />
              </Avatar>
            ) : (
              <Avatar>
                <AvatarFallback>
                  <IconRound fill="currentColor" className="size-full" />
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        )}
        <div
          className={cn('flex min-w-0 flex-1 flex-col items-start', isHeaderClickable && 'cursor-pointer')}
          onClick={() => onHeaderClicked()}
        >
          <h4>{name}</h4>
          {currentUser?.is_caregiver && conversationMetadata?.conversation.patient.access_tier && (
            <PatientAccessTierTag accessTier={conversationMetadata.conversation.patient.access_tier} />
          )}
        </div>
      </div>
      {showActionsRow && (
        <>
          <div className="flex w-full flex-nowrap items-center bg-inverse px-3 py-4">
            {messageSearch.open ? (
              <>
                <div className="relative min-w-0 flex-1">
                  <SearchIcon
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 left-1 size-4 -translate-y-1/2 text-inverse-foreground/75"
                  />
                  <Input
                    type="text"
                    autoFocus
                    placeholder={t('chats.actions.searchMessagesPlaceholder')}
                    value={messageSearch.query}
                    onChange={e => messageSearch.onQueryChange(e.target.value)}
                    aria-label={t('chats.actions.searchMessages')}
                    className="rounded-none border-0 border-b border-info-muted bg-inverse pl-7 text-inverse-foreground shadow-none placeholder:text-inverse-foreground/75 focus-visible:border-info-muted focus-visible:ring-0"
                  />
                </div>
                <div className="ml-2 flex shrink-0 items-center">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={actionButtonClassName}
                    aria-label={t('chats.actions.closeMessageSearch')}
                    onClick={() => messageSearch.onClose()}
                  >
                    <CloseOutlined />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex min-w-0 flex-1 items-center justify-evenly">{primaryActionsBar}</div>
            )}
          </div>
          {messageSearch.open && messageSearch.matchCount > 0 ? (
            <div className="pointer-events-none relative z-[101] h-0 w-full">
              <div className="pointer-events-auto absolute top-2.5 right-3 flex flex-row items-center gap-2.5">
                <span
                  className="rounded-full bg-card/95 px-3 py-2 text-sm leading-tight font-semibold whitespace-nowrap text-foreground shadow-md"
                  data-testid="message-search-match-pill"
                >
                  {t('chats.actions.searchMatchCounter', {
                    current: messageSearch.matchIndex + 1,
                    total: messageSearch.matchCount,
                  })}
                </span>
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="icon-lg"
                    aria-label={t('chats.actions.searchPreviousMatch')}
                    data-testid="message-search-prev-bar"
                    onClick={() => messageSearch.onPrevMatch()}
                  >
                    <ArrowUpOutlined />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon-lg"
                    aria-label={t('chats.actions.searchNextMatch')}
                    data-testid="message-search-next-bar"
                    onClick={() => messageSearch.onNextMatch()}
                  >
                    <ArrowDownOutlined />
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
};
export default ConversationTopBar;
